import moment from "moment";
import { CATEGORIES, RESIDENTIAL_LABELS } from "../calc/shared/haklaiConstants";
import { isHaklaiPre1997MinimalZabanRelevantForPart } from "../calc/shared/haklaiFlowHelpers";
import { isHaklaiPre1997AcquisitionDateEligible } from "../calc/preCalc/logic/haklaiPreCalcHelpers";
import {
  createHaklaiPropertyPart,
  normalizeHaklaiPropertyPartsData
} from "../calc/propertyParts/logic/haklaiPropertyPartsHelpers";
import {
  hasHaklaiSelectablePurchaseDepreciationProfile,
  normalizeHaklaiDepreciationState,
  normalizeHaklaiResidentialRentalByPart
} from "../calc/shared/haklaiDepreciationHelpers";
import {
  normalizeHaklaiPurchaseZabanAttributionByPart,
  normalizeExpensesForState,
  normalizeHaklaiExpensesForDependencies
} from "../calc/expenses/logic/haklaiExpensesHelpers";
import {
  createInitialHaklaiCalc,
  emptyInitialHaklaiCalc,
  initialHaklaiManage
} from "./haklaiInitialState";

const parseHaklaiManageBoolean = (value) => (
  value === true || value === "true" || value === 1 || value === "1"
);

export const parseHaklaiManageFromServer = (manageData = {}) => ({
  ...initialHaklaiManage,
  ...manageData,
  dealDescription: manageData.dealDescription ? manageData.dealDescription : "",
  hasEaaratAzhara: parseHaklaiManageBoolean(manageData.hasEaaratAzhara),
  hasMasShevachReportsSubmitted: parseHaklaiManageBoolean(manageData.hasMasShevachReportsSubmitted),
  masShavach: parseHaklaiManageBoolean(manageData.masShavach),
  masRechisha: parseHaklaiManageBoolean(manageData.masRechisha),
  haitelHashbacha: parseHaklaiManageBoolean(manageData.haitelHashbacha)
});

export const createInitialHaklaiCalcWithResidence = () => {
  const initialCalc = createInitialHaklaiCalc();
  return {
    ...initialCalc,
    mainCalc: {
      ...initialCalc.mainCalc,
      propertyPartsData: {
        "residence_1": {
          ...createHaklaiPropertyPart({
            category: CATEGORIES.RESIDENTIAL,
            label: RESIDENTIAL_LABELS[0]
          })
        }
      },
      expenses: normalizeExpensesForState([]),
      residentialRentalByPart: {},
      purchaseDepreciationByPart: {},
      purchaseDepreciationSelectionByPart: {},
      purchaseZabanAttributionByPart: {}
    }
  };
};

const getHaklaiServerCalcSources = (calc = {}) => {
  if (calc.haklaiRaw?.preCalc) {
    return {
      serverPreCalc: calc.haklaiRaw.preCalc,
      serverMainCalc: calc.haklaiRaw.mainCalc || {}
    };
  }
  if (calc.preCalc) {
    return {
      serverPreCalc: calc.preCalc,
      serverMainCalc: calc.mainCalc || {}
    };
  }
  return { serverPreCalc: null, serverMainCalc: {} };
};

const parseHaklaiPrisa = (serverMainCalc = {}) => ({
  shouldTryPrisa: serverMainCalc.prisa?.shouldTryPrisa ? true : false,
  personsData: Array.isArray(serverMainCalc.prisa?.personsData)
    ? serverMainCalc.prisa.personsData.map((person) => ({
      personName: person?.personName || "",
      isMaximal: person?.isMaximal ?? true,
      personOwningPercent: Number.isFinite(person?.personOwningPercent) ? person.personOwningPercent : 1,
      birthOfClient: person?.birthOfClient || "",
      creditPointGender: person?.creditPointGender || "",
      creditPointResidency: person?.creditPointResidency || "",
      dataPerYear: Array.isArray(person?.dataPerYear)
        ? person.dataPerYear.map((year) => ({
          income: Number.isFinite(year?.income) ? year.income : null,
          zikui: Number.isFinite(year?.zikui) ? year.zikui : 0
        }))
        : []
    }))
    : []
});

const applyLegacyMainResidence = (propertyPartsData) => {
  const hasResidential = Object.values(propertyPartsData).some(
    (p) => p.category === CATEGORIES.RESIDENTIAL
  );
  if (hasResidential) return propertyPartsData;

  propertyPartsData["residence_1"] = {
    ...createHaklaiPropertyPart({
      category: CATEGORIES.RESIDENTIAL,
      label: RESIDENTIAL_LABELS[0]
    })
  };
  return propertyPartsData;
};

const applyLegacyPre1997Flag = ({ serverPreCalc, parsedCalc, propertyPartsData }) => {
  if (
    serverPreCalc.cosnstructionsDoneBeforeApril1997 !== true
    || !isHaklaiPre1997AcquisitionDateEligible(parsedCalc.preCalc)
  ) {
    return propertyPartsData;
  }

  const legacyPre1997Target = Object.entries(propertyPartsData).find(
    ([, part]) => isHaklaiPre1997MinimalZabanRelevantForPart(
      part,
      parsedCalc.preCalc.hasZaban === true
    )
  );
  if (!legacyPre1997Target) return propertyPartsData;

  const [partKey, part] = legacyPre1997Target;
  propertyPartsData[partKey] = {
    ...part,
    constructionsDoneBeforeApril1997: true
  };
  return propertyPartsData;
};

export const parseHaklaiCalcFromServer = ({ serverPreCalc, serverMainCalc }) => {
  if (!serverPreCalc) return null;

  const propertyPartsDataFromServer = normalizeHaklaiPropertyPartsData(serverMainCalc.propertyPartsData || {});
  const purchaseDepreciationByPartFromServer =
    serverMainCalc.purchaseDepreciationByPart
    && typeof serverMainCalc.purchaseDepreciationByPart === "object"
      ? Object.entries(serverMainCalc.purchaseDepreciationByPart).reduce((acc, [partKey, depreciationState]) => {
        acc[partKey] = normalizeHaklaiDepreciationState(depreciationState);
        return acc;
      }, {})
      : {};
  const residentialRentalByPartFromServer = normalizeHaklaiResidentialRentalByPart(
    serverMainCalc.residentialRentalByPart,
    propertyPartsDataFromServer
  );
  const purchaseDepreciationSelectionByPartFromServer =
    serverMainCalc.purchaseDepreciationSelectionByPart
    && typeof serverMainCalc.purchaseDepreciationSelectionByPart === "object"
    && !Array.isArray(serverMainCalc.purchaseDepreciationSelectionByPart)
      ? Object.entries(serverMainCalc.purchaseDepreciationSelectionByPart).reduce((acc, [partKey, isSelected]) => {
        const part = propertyPartsDataFromServer[partKey];
        const hasPurchaseProfile = hasHaklaiSelectablePurchaseDepreciationProfile({
          partKey,
          part,
          residentialRentalByPart: residentialRentalByPartFromServer,
          purchaseDepreciationByPart: purchaseDepreciationByPartFromServer
        });
        if (part && isSelected === true && hasPurchaseProfile) {
          acc[partKey] = true;
        }
        return acc;
      }, {})
      : {};
  const purchaseZabanAttributionByPartFromServer = normalizeHaklaiPurchaseZabanAttributionByPart(
    serverMainCalc.purchaseZabanAttributionByPart,
    propertyPartsDataFromServer,
    serverPreCalc.hasZaban === true
  );

  const parsedCalc = {
    preCalc: {
      sellerType: serverPreCalc.sellerType || emptyInitialHaklaiCalc.preCalc.sellerType,
      soldPropertyDate: serverPreCalc.soldPropertyDate || emptyInitialHaklaiCalc.preCalc.soldPropertyDate,
      totalPropertyCost: serverPreCalc.totalPropertyCost ?? emptyInitialHaklaiCalc.preCalc.totalPropertyCost,
      hasZaban: serverPreCalc.hasZaban ?? emptyInitialHaklaiCalc.preCalc.hasZaban,
      ptorType: serverPreCalc.ptorType || emptyInitialHaklaiCalc.preCalc.ptorType,
      hasTzinunOnPtor: serverPreCalc.hasTzinunOnPtor ?? emptyInitialHaklaiCalc.preCalc.hasTzinunOnPtor,
      wayOfGettingProperty: serverPreCalc.wayOfGettingProperty || emptyInitialHaklaiCalc.preCalc.wayOfGettingProperty,
      acquisitionDetails: {
        deduDate: serverPreCalc.acquisitionDetails?.deduDate || emptyInitialHaklaiCalc.preCalc.acquisitionDetails.deduDate,
        testatorPassingDate: serverPreCalc.acquisitionDetails?.testatorPassingDate || emptyInitialHaklaiCalc.preCalc.acquisitionDetails.testatorPassingDate,
        testatorBoughtPropertyDate: serverPreCalc.acquisitionDetails?.testatorBoughtPropertyDate || emptyInitialHaklaiCalc.preCalc.acquisitionDetails.testatorBoughtPropertyDate,
        amount: serverPreCalc.acquisitionDetails?.amount ?? emptyInitialHaklaiCalc.preCalc.acquisitionDetails.amount,
        deduCurrency: serverPreCalc.acquisitionDetails?.deduCurrency || emptyInitialHaklaiCalc.preCalc.acquisitionDetails.deduCurrency,
        hasRabinovich: serverPreCalc.acquisitionDetails?.hasRabinovich || emptyInitialHaklaiCalc.preCalc.acquisitionDetails.hasRabinovich
      },
      parcelGush: serverPreCalc?.parcelGush ?? emptyInitialHaklaiCalc.preCalc.parcelGush,
      parcelHelka: serverPreCalc?.parcelHelka ?? emptyInitialHaklaiCalc.preCalc.parcelHelka,
      parcelAddressSearch: serverPreCalc?.parcelAddressSearch ?? emptyInitialHaklaiCalc.preCalc.parcelAddressSearch,
      parcelLookupError: null
    },
    mainCalc: {
      propertyPartsData: propertyPartsDataFromServer,
      expenses: Array.isArray(serverMainCalc.expenses) ? serverMainCalc.expenses : [],
      residentialRentalByPart: residentialRentalByPartFromServer,
      purchaseDepreciationByPart: purchaseDepreciationByPartFromServer,
      purchaseDepreciationSelectionByPart: purchaseDepreciationSelectionByPartFromServer,
      purchaseZabanAttributionByPart: purchaseZabanAttributionByPartFromServer,
      prisa: parseHaklaiPrisa(serverMainCalc),
      kizuzIski: Number.isFinite(serverMainCalc.kizuzIski) ? serverMainCalc.kizuzIski : null,
      kizuzHoni: Number.isFinite(serverMainCalc.kizuzHoni) ? serverMainCalc.kizuzHoni : null,
      isGilum: serverMainCalc.isGilum ? true : false,
      afkaa: serverMainCalc.afkaa ? true : false
    }
  };

  const propertyPartsData = applyLegacyPre1997Flag({
    serverPreCalc,
    parsedCalc,
    propertyPartsData: applyLegacyMainResidence(parsedCalc.mainCalc.propertyPartsData)
  });

  parsedCalc.mainCalc.expenses = normalizeExpensesForState(parsedCalc.mainCalc.expenses);
  parsedCalc.mainCalc.expenses = normalizeHaklaiExpensesForDependencies(
    parsedCalc.mainCalc.expenses,
    propertyPartsData,
    parsedCalc.preCalc.hasZaban === true
  ).expenses;

  return parsedCalc;
};

export const hydrateHaklaiDealData = (dealData = {}) => {
  const manage = dealData.manage ? parseHaklaiManageFromServer(dealData.manage) : initialHaklaiManage;
  const savedHaklaiAnalysis = dealData.calc?.haklaiAnalysis || null;
  const analyzedClassicCalc = {
    preCalc: dealData.calc?.preCalc || null,
    mainCalc: dealData.calc?.mainCalc || null
  };
  const { serverPreCalc, serverMainCalc } = getHaklaiServerCalcSources(dealData.calc || {});
  const parsedCalc = parseHaklaiCalcFromServer({ serverPreCalc, serverMainCalc });

  if (parsedCalc) {
    return {
      prettyId: dealData.prettyId || null,
      manage,
      calc: parsedCalc,
      haklaiAnalysis: savedHaklaiAnalysis,
      analyzedClassicCalc,
      hasSavedHaklaiAnalysis: Boolean(savedHaklaiAnalysis),
      dealDate: parsedCalc.preCalc.soldPropertyDate || moment(dealData.dateCreated).format("YYYY-MM-DD")
    };
  }

  const newCalc = createInitialHaklaiCalcWithResidence();
  return {
    prettyId: dealData.prettyId || null,
    manage,
    calc: newCalc,
    haklaiAnalysis: savedHaklaiAnalysis,
    analyzedClassicCalc,
    hasSavedHaklaiAnalysis: false,
    dealDate: newCalc.preCalc.soldPropertyDate || moment(dealData.dateCreated).format("YYYY-MM-DD")
  };
};
